// 事件相关的 hooks
import { useEffect, useRef, useState, useMemo } from "react";
import { wsUtils } from "../utils/wsUtils";
import { PROXY_ACTIVATED_EVENT } from "../modules/domainGuard";
import type { BetData } from "../types/wsUtils.types";
import { useStartupData } from "../modules/startup/stores";

// 重新导出类型
export type { BetData, OnlineData } from "../types/wsUtils.types";

export function useGameData(gameId: string, callback: (data: any) => void) {
  return useEvent("game-data", (data: any) => {
    if (data.gameData.gameId !== gameId) return;
    callback(data);
  });
}

export function useBetData(gameId: string, callback: (data: BetData) => void) {
  return useEvent("bet", (data: BetData) => {
    const bet = data.bet;
    if (bet.gameId !== gameId) return;
    callback(data);
  });
}

export function useBetBeginData(
  gameId: string,
  callback: (data: BetData) => void
) {
  return useEvent("bet-begin", (data: BetData) => {
    const bet = data.bet;
    if (bet.gameId !== gameId) return;
    callback(data);
  });
}

export function useBetWinData(
  gameId: string,
  callback: (data: BetData) => void
) {
  return useEvent("bet-win", (data: BetData) => {
    const bet = data.bet;
    if (bet.gameId !== gameId) return;
    callback(data);
  });
}

export function useEvent(event: string, callback: (...args: any[]) => void) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = (...args: any[]) => {
      callbackRef.current(...args);
    };

    // 从 WebSocket 监听事件
    wsUtils.on(event, handler);

    return () => {
      wsUtils.off(event, handler);
    };
  }, [event]);
}

// WebSocket 模块订阅管理
const totalModules: string[] = [];

export function useWs(modules: string) {
  useEffect(() => {
    totalModules.push(modules);

    const ids = [
      ...new Set(
        totalModules.flatMap(m =>
          m
            .split(",")
            .map(x => x.trim())
            .filter(x => x)
        )
      ),
    ]
      .sort()
      .join(",");

    wsUtils.open(ids);

    return () => {
      const i = totalModules.indexOf(modules);
      totalModules.splice(i, 1);

      const ids = [
        ...new Set(
          totalModules.flatMap(m =>
            m
              .split(",")
              .map(x => x.trim())
              .filter(x => x)
          )
        ),
      ]
        .sort()
        .join(",");
      wsUtils.open(ids);
    };
  }, [modules]);
}

// 初始化 WebSocket 连接的 hook
export function useWsInit(modules?: string) {
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initWs = async () => {
      try {
        console.log("Initializing WebSocket connection...");
        await wsUtils.open(modules);
        console.log("WebSocket connection initialized");
      } catch (error) {
        console.error("Failed to initialize WebSocket:", error);
      }
    };

    initWs();

    return () => {
      // 清理函数可以选择性关闭 WebSocket
      // wsUtils.close();
    };
  }, [modules]);

  // 域名容灾软恢复：主域名被封时，首波的 /api/sockmsg/init 会失败，
  // init() 在创建 socket 之前就抛错 → this.ws 从未存在 → socket.io 的自动重连无从触发，
  // 而上面的 hasInitialized 又锁死了重试，结果是整个会话都收不到实时消息。
  // 代理切到备用域名后补一次 ensureConnected（幂等，已连接时是空操作）。
  useEffect(() => {
    const onProxyActivated = () => {
      wsUtils.ensureConnected().catch(e => {
        console.warn("[domainGuard] WebSocket 软恢复失败:", e);
      });
    };
    window.addEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    return () => {
      window.removeEventListener(PROXY_ACTIVATED_EVENT, onProxyActivated);
    };
  }, []);

  return wsUtils.connected();
}

// WebSocket 连接状态 hook
export function useWsConnected() {
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const checkConnection = () => {
      setConnected(wsUtils.connected());
    };

    const interval = setInterval(checkConnection, 1000);
    checkConnection();

    return () => {
      clearInterval(interval);
    };
  }, []);

  return connected;
}

// export function useOnlineData(): OnlineData {
//   const [onlineData, setOnlineData] = useState<OnlineData>({ online: 0 });

//   useEffect(() => {
//     const handleOnlineData = (data: OnlineData) => {
//       setOnlineData(data);
//     };

//     // 使用 WebSocket 监听在线数据useOnlineData事件
//     wsUtils.on("online-data", handleOnlineData);

//     return () => {
//       wsUtils.off("online-data", handleOnlineData);
//     };
//   }, []);

//   return onlineData;
// }

// 在线数据 hook - 集成品牌信息和默认投注金额
export interface EnhancedOnlineData {
  players: number;
  winners: number;
  award: number;
}

export function useOnlineData(): EnhancedOnlineData {
  const [players, setPlayers] = useState(0);
  const [winners, setWinners] = useState(0);
  const [award, setAward] = useState(0);

  const { data: startupData } = useStartupData();
  const brand = startupData?.brand;
  const defaultBetAmount = startupData?.defaultBetAmount || 0;

  // 订阅全局 WebSocket 模块
  useWs("global");

  // 初始化品牌数据
  useEffect(() => {
    if (brand) {
      setPlayers(brand.players || 0);
      setWinners(brand.winners || 0);
    }
  }, [brand]);

  // 监听全局在线数据事件
  useEvent("global", (data: any) => {
    if (brand && data.data && data.data.user) {
      setPlayers((brand.players || 0) + data.data.user);
      setWinners((brand.winners || 0) + data.data.user);
    }
  });

  // 计算奖金
  useEffect(() => {
    if (!players || !defaultBetAmount) return;
    setAward(players * defaultBetAmount * 10);
  }, [players, defaultBetAmount]);

  // 使用 useMemo 优化返回值
  const memValue = useMemo(
    () => ({ players, winners, award }),
    [players, winners, award]
  );

  return memValue;
}

export function useGameTerm(gameId: string, callback: (term: any) => void) {
  return useEvent("game-term", (data: any) => {
    if (data.gameId !== gameId) return;
    callback(data.term);
  });
}

// 游戏期数追踪 hook - 基于 useGameData
let preTermNo: string | undefined;
let preTermFinished: string | undefined;

export function useGameTermTracking(
  gameId: string,
  callback: (term: any) => void
) {
  useGameData(gameId, (data: any) => {
    const gameData = data.gameData;
    if (gameData.gameId !== gameId) {
      return;
    }

    const term = gameData.data?.term;
    if (!term) {
      return;
    }

    // 追踪新期数
    if (term.status === "NEW") {
      if (term.id !== preTermNo) {
        preTermNo = term.id;
        callback(term);
      }
    }
    // 追踪已完成的期数
    else if (term.status === "FINISHED") {
      if (term.id !== preTermFinished) {
        preTermFinished = term.id;
        callback(term);
      }
    }
  });
}
